import React from "react";
import {
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { extractGuidanceForAttachment } from "../lib/extractGuidance";

export const ORIGINAL_NOT_STORED_LABEL = "Origineel niet opgeslagen";

/** User-facing extract labels (separate from storage status). */
export const EXTRACT_STATUS_COPY = {
  ok: null,
  empty: "Geen leesbare tekst. Document niet volledig te controleren",
  truncated: "Tekst afgekapt door limiet. Document niet volledig te controleren",
  partial: "Document niet volledig leesbaar. Niet volledig te controleren",
  error: "Tekstextractie mislukt. Document niet volledig te controleren",
  unknown: "Extractiestatus onbekend. Document niet volledig te controleren",
};

export function kindIcon(kind) {
  if (kind === "image") return ImageIcon;
  if (kind === "xlsx" || kind === "csv") return FileSpreadsheet;
  if (kind === "text") return FileIcon;
  return FileText;
}

/** True when the original bytes were not kept in object storage (UI must say so). */
export function originalNotStored(att) {
  if (!att) return false;
  if (att.storage === "unavailable" || att.storage_status === "unavailable") {
    return true;
  }
  // Legacy / partial payloads: no path and not explicitly ok.
  if (att.storage_path == null && att.storage !== "ok" && att.storage_status !== "ok") {
    if (att.storage_reason || att.has_text != null) return true;
  }
  return false;
}

/** Extract status for gating UI; legacy missing → unknown (never treat as ok). */
export function extractStatusOf(att) {
  if (!att) return "unknown";
  const raw = att.extract_status;
  if (raw == null || raw === "") return "unknown";
  return String(raw);
}

export function extractIncomplete(att) {
  if (!att) return true;
  if (att.extract_complete === true) return false;
  if (att.extract_complete === false) return true;
  return extractStatusOf(att) !== "ok";
}

export function extractStatusMessage(att) {
  if (!extractIncomplete(att)) return null;
  const guidance = extractGuidanceForAttachment(att);
  if (guidance?.summary) return guidance.summary;
  if (att?.extract_label && extractStatusOf(att) !== "ok") return att.extract_label;
  return EXTRACT_STATUS_COPY[extractStatusOf(att)] || EXTRACT_STATUS_COPY.unknown;
}

export function AttachmentChip({ att, onRemove, onReplace, testid }) {
  const Icon = kindIcon(att?.kind);
  const missingOriginal = originalNotStored(att);
  const incomplete = extractIncomplete(att);
  const extractMsg = extractStatusMessage(att);
  const guidance = incomplete ? extractGuidanceForAttachment(att) : null;
  return (
    <span
      data-testid={testid}
      className="inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-lg border border-subtle bg-elevated px-2 py-1 text-[11px] text-slate-300"
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-brand" />
      <span className="max-w-[160px] truncate">{att?.original_filename || att?.filename}</span>
      {extractMsg ? (
        <span data-testid="attachment-extract-status" className="font-mono text-[9px] text-warn">
          {extractMsg}
        </span>
      ) : null}
      {guidance?.coverageLine ? (
        <span data-testid="attachment-extract-coverage" className="font-mono text-[9px] text-slate-400">
          {guidance.coverageLine}
        </span>
      ) : null}
      {missingOriginal && (
        <span data-testid="attachment-original-missing" className="font-mono text-[9px] text-warn">
          {ORIGINAL_NOT_STORED_LABEL}
        </span>
      )}
      {incomplete && onReplace && (
        <button
          type="button"
          data-testid="attachment-replace"
          onClick={onReplace}
          className="rounded border border-warn/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-warn hover:bg-warn/10"
        >
          Vervang
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          data-testid="attachment-remove"
          onClick={onRemove}
          className="ml-0.5 rounded p-0.5 text-slate-500 hover:text-block"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
